import {
  AppModal,
  Button,
  Input,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import type { MatterTemplate } from "@/lib/types";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

interface SelectTemplateSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  templates: MatterTemplate[];
  isLoading?: boolean;
  onTemplateSelected: (template: MatterTemplate) => void;
}

export const SelectTemplateSlideover = ({
  open,
  setOpen,
  templates,
  isLoading = false,
  onTemplateSelected,
}: SelectTemplateSlideoverProps) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredTemplates = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();

    return templates
      .slice()
      .sort((templateA, templateB) =>
        templateA.name.localeCompare(templateB.name),
      )
      .filter((template) => {
        if (!normalizedSearchValue) {
          return true;
        }

        return template.name.toLowerCase().includes(normalizedSearchValue);
      });
  }, [searchValue, templates]);

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="Select template"
      description="Choose a template to use for this signature request."
      size="2xl"
      overlayClassName="z-[102] bg-black/45 backdrop-blur-md"
      className="z-[103]"
      footer={
        <div className="flex w-full justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 grow flex-col overflow-hidden">
        <div className="p-6">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
              placeholder="Search templates"
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed p-6 text-center">
              <Typography className="text-muted-foreground text-sm">
                No templates found.
              </Typography>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTemplates.map((template) => (
                <Button
                  key={template.id}
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-between px-4 py-3 text-left"
                  onClick={() => {
                    onTemplateSelected(template);
                    setOpen(false);
                    setSearchValue("");
                  }}
                >
                  <div className="min-w-0">
                    <Typography className="truncate text-sm font-medium">
                      {getFileNameParts(template.name).fileName ||
                        template.name}
                    </Typography>
                    <Typography className="text-muted-foreground mt-0.5 truncate text-xs">
                      {template.name}
                    </Typography>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
};
